import { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { api, downloadBlob } from '../../api/client'
import type { MedEsito, MedMisure, MedPiano, MedProfiloModello, MedStatoScadenza, MedTipoVerifica, MedVerifica } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { livelloModulo } from '../../auth/permessi'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, StatCard, formatDay } from '../common'
import { DataTable } from '../DataTable'
import { Finestra } from '../ModificheGitlab'
import { BadgeStatoMed, classiElettriche, etichettaStatoMed, partiApplicate } from './comuni'

/**
 * CENSO MED (D7): maintenance and checks. Plans per device (how often, next
 * due date), checks done with their outcome, CEI EN 62353 measurements and
 * the PDF report, the model's defaults, the due dates of the whole area and
 * who gets the reminders. CENSO does not judge the measurements: the outcome
 * is the technician's; a failed check proposes «out of service», a person
 * decides.
 */

export const TIPI_VERIFICA: MedTipoVerifica[] = ['preventive', 'electrical_safety', 'calibration', 'functional', 'other']

export function etichettaTipoVerifica(k: string): string {
  const e: Record<string, string> = {
    preventive: t('Manutenzione preventiva'), electrical_safety: t('Sicurezza elettrica (CEI EN 62353)'),
    calibration: t('Taratura'), functional: t('Verifica funzionale'), other: t('Altra verifica'),
  }
  return e[k] ?? k
}

export function etichettaEsito(k: string): string {
  const e: Record<string, string> = { pass: t('Superata'), conditional: t('Superata con riserva'), fail: t('Non superata') }
  return e[k] ?? k
}

function BadgeEsito({ esito }: { esito: string }) {
  const tono = esito === 'pass' ? 'ok' : esito === 'conditional' ? 'warn' : 'error'
  return <span className={`badge badge--${tono}`}>{etichettaEsito(esito)}</span>
}

function etichettaScadenza(s: MedStatoScadenza): string {
  const e: Record<MedStatoScadenza, string> = {
    overdue: t('Scaduta'), due_30: t('Entro 30 giorni'), due_60: t('Entro 60 giorni'), due_90: t('Entro 90 giorni'),
    later: t('Più avanti'), none: t('Senza data'),
  }
  return e[s]
}

function BadgeScadenza({ stato }: { stato: MedStatoScadenza }) {
  const tono = stato === 'overdue' ? 'error' : stato === 'due_30' ? 'warn' : stato === 'none' ? 'muted' : 'info'
  return <span className={`badge badge--${tono}`}>{etichettaScadenza(stato)}</span>
}

const MISURE: { k: keyof MedMisure; label: () => string }[] = [
  { k: 'protective_earth_ohm', label: () => t('Resistenza del conduttore di protezione (Ω)') },
  { k: 'equipment_leakage_ua', label: () => t('Corrente di dispersione dell’apparecchio (µA)') },
  { k: 'applied_part_leakage_ua', label: () => t('Corrente di dispersione della parte applicata (µA)') },
  { k: 'insulation_mohm', label: () => t('Resistenza di isolamento (MΩ)') },
]

function etichettaMetodo(m: string): string {
  const e: Record<string, string> = { direct: t('diretto'), differential: t('differenziale'), alternative: t('alternativo') }
  return e[m] ?? m
}

function riassuntoMisure(m: MedMisure): string {
  const parti = MISURE.filter((x) => m[x.k] !== undefined).map((x) => `${x.label()}: ${m[x.k]}`)
  if (m.method) parti.push(`${t('metodo')} ${etichettaMetodo(m.method)}`)
  return parti.join(' · ')
}

function useScrive(): boolean {
  const { principal } = useAuth()
  return livelloModulo(principal, 'medical') === 'write'
}

function FinestraPiano({ deviceId, piano, onChiudi, onFatto }: {
  deviceId: string
  piano: MedPiano | null
  onChiudi: () => void
  onFatto: () => void
}) {
  const contratti = useAsync(() => api.medContracts(), [])
  const [v, setV] = useState({
    kind: piano?.kind ?? 'electrical_safety', interval_months: String(piano?.interval_months ?? 12),
    next_due: piano?.next_due ?? '', contract_id: piano?.contract_id ?? '', supplier: piano?.supplier ?? '',
    notes: piano?.notes ?? '', active: piano?.active ?? true,
  })
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  async function salva() {
    setErrore(null)
    setLavoro(true)
    try {
      await api.medPlanSave(deviceId, {
        kind: v.kind, interval_months: Number(v.interval_months), next_due: v.next_due || null,
        contract_id: v.contract_id || null, supplier: v.supplier.trim() || null, notes: v.notes.trim() || null, active: v.active,
      })
      onFatto()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Finestra titolo={piano ? t('Modifica il piano') : t('Nuovo piano di verifica')} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="form-dialog">
        <div className="form-grid">
          <label className="field"><span className="field__label">{t('Tipo')}</span>
            <select value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value as MedTipoVerifica })} disabled={Boolean(piano)}>
              {TIPI_VERIFICA.map((k) => <option key={k} value={k}>{etichettaTipoVerifica(k)}</option>)}
            </select></label>
          <label className="field span-1"><span className="field__label">{t('Ogni (mesi)')}</span>
            <input type="number" min={1} max={120} value={v.interval_months} onChange={(e) => setV({ ...v, interval_months: e.target.value })} /></label>
          <label className="field"><span className="field__label">{t('Prossima scadenza')}</span>
            <input type="date" value={v.next_due} onChange={(e) => setV({ ...v, next_due: e.target.value })} />
            <span className="field__hint">{t('Vuota: oggi, o dall’ultima verifica dello stesso tipo. Poi la sposta ogni verifica registrata.')}</span></label>
          <label className="field span-3"><span className="field__label">{t('Contratto')}</span>
            <select value={v.contract_id} onChange={(e) => setV({ ...v, contract_id: e.target.value })}>
              <option value="">{t('nessuno')}</option>
              {(contratti.data?.contracts ?? []).map((c) => <option key={c.id} value={c.id}>{c.reference} · {c.vendor}</option>)}
            </select></label>
          <label className="field span-3"><span className="field__label">{t('Chi la fa')}</span>
            <input type="text" value={v.supplier} onChange={(e) => setV({ ...v, supplier: e.target.value })} maxLength={200}
                   placeholder={t('il fornitore o l’Ingegneria clinica')} /></label>
          <label className="field span-6"><span className="field__label">{t('Note')}</span>
            <textarea rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} maxLength={4000} /></label>
          <div className="checkbox span-6">
            <input id="med-piano-attivo" type="checkbox" checked={v.active} onChange={(e) => setV({ ...v, active: e.target.checked })} />
            <label htmlFor="med-piano-attivo">{t('Attivo')}<small>{t('Un piano sospeso non compare nelle scadenze e non manda promemoria.')}</small></label>
          </div>
        </div>
        <div className="form-dialog__azioni">
          <button type="button" className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Annulla')}</button>
          <button type="button" onClick={() => void salva()} disabled={lavoro || !(Number(v.interval_months) >= 1)}>{t('Salva')}</button>
        </div>
      </div>
    </Finestra>
  )
}

function FinestraVerifica({ deviceId, onChiudi, onFatto }: {
  deviceId: string
  onChiudi: () => void
  onFatto: (fuoriServizio: boolean) => void
}) {
  const oggi = new Date().toISOString().slice(0, 10)
  const [v, setV] = useState({ kind: 'electrical_safety' as MedTipoVerifica, performed_on: oggi, performed_by: '',
    outcome: 'pass' as MedEsito, notes: '' })
  const [misure, setMisure] = useState<Record<string, string>>({ method: '' })
  const [rapporto, setRapporto] = useState<File | null>(null)
  const [revisione, setRevisione] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const sicurezza = v.kind === 'electrical_safety'

  async function registra() {
    setErrore(null)
    setLavoro(true)
    try {
      const m = sicurezza ? Object.fromEntries(Object.entries(misure).filter(([, x]) => x !== '')
        .map(([k, x]) => [k, k === 'method' ? x : Number(x)])) : {}
      const r = await api.medCheckRecord(deviceId, { ...v, performed_by: v.performed_by.trim() || null,
        notes: v.notes.trim() || null, measurements: m })
      if (rapporto) await api.medCheckFileUpload(r.id, rapporto, rapporto.name)
      onFatto(r.propose_out_of_service)
    } catch (e) {
      setErrore(e)
      setRevisione(false)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Finestra titolo={t('Registra una verifica')} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="form-dialog">
        {revisione ? (
          <>
            <dl className="kv">
              <dt>{t('Tipo')}</dt><dd>{etichettaTipoVerifica(v.kind)}</dd>
              <dt>{t('Data')}</dt><dd>{formatDay(v.performed_on)}</dd>
              <dt>{t('Eseguita da')}</dt><dd>{v.performed_by || '—'}</dd>
              <dt>{t('Esito')}</dt><dd><BadgeEsito esito={v.outcome} /></dd>
              {sicurezza && <><dt>{t('Misure')}</dt><dd>{riassuntoMisure(Object.fromEntries(Object.entries(misure)
                .filter(([, x]) => x !== '').map(([k, x]) => [k, k === 'method' ? x : Number(x)])) as MedMisure) || '—'}</dd></>}
              <dt>{t('Rapporto')}</dt><dd>{rapporto?.name ?? '—'}</dd>
            </dl>
            <p className="field__hint">{t('La prossima scadenza del piano dello stesso tipo si sposta dalla data di questa verifica.')}</p>
            <div className="form-dialog__azioni">
              <button type="button" className="secondary" onClick={() => setRevisione(false)} disabled={lavoro}>{t('Indietro')}</button>
              <button type="button" onClick={() => void registra()} disabled={lavoro}>{lavoro ? t('Salvo…') : t('Registra')}</button>
            </div>
          </>
        ) : (
          <>
            <div className="form-grid">
              <label className="field"><span className="field__label">{t('Tipo')}</span>
                <select value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value as MedTipoVerifica })}>
                  {TIPI_VERIFICA.map((k) => <option key={k} value={k}>{etichettaTipoVerifica(k)}</option>)}
                </select></label>
              <label className="field"><span className="field__label">{t('Data')}</span>
                <input type="date" value={v.performed_on} max={oggi} onChange={(e) => setV({ ...v, performed_on: e.target.value })} /></label>
              <label className="field"><span className="field__label">{t('Esito')}</span>
                <select value={v.outcome} onChange={(e) => setV({ ...v, outcome: e.target.value as MedEsito })}>
                  {(['pass', 'conditional', 'fail'] as const).map((k) => <option key={k} value={k}>{etichettaEsito(k)}</option>)}
                </select></label>
              <label className="field span-6"><span className="field__label">{t('Eseguita da')}</span>
                <input type="text" value={v.performed_by} onChange={(e) => setV({ ...v, performed_by: e.target.value })} maxLength={200}
                       placeholder={t('tecnico e ditta')} /></label>
            </div>
            {sicurezza && (
              <>
                <h4 className="form__sezione">{t('Misure (come le dà lo strumento)')}</h4>
                <div className="form-grid">
                  {MISURE.map((x) => (
                    <label key={x.k} className="field span-3"><span className="field__label">{x.label()}</span>
                      <input type="number" min={0} step="any" value={misure[x.k] ?? ''}
                             onChange={(e) => setMisure({ ...misure, [x.k]: e.target.value })} /></label>
                  ))}
                  <label className="field"><span className="field__label">{t('Metodo')}</span>
                    <select value={misure.method} onChange={(e) => setMisure({ ...misure, method: e.target.value })}>
                      <option value="">{t('non indicato')}</option>
                      {(['direct', 'differential', 'alternative'] as const).map((m) => <option key={m} value={m}>{etichettaMetodo(m)}</option>)}
                    </select></label>
                </div>
                <p className="field__hint">{t('CENSO non giudica le misure: i limiti dipendono da classe, parte applicata e metodo, e l’esito è del tecnico.')}</p>
              </>
            )}
            <h4 className="form__sezione">{t('Rapporto e note')}</h4>
            <div className="form-grid">
              <div className="field span-6">
                <input ref={input} type="file" accept="application/pdf" style={{ display: 'none' }}
                       onChange={(e) => setRapporto(e.target.files?.[0] ?? null)} />
                <span className="med-opzioni">
                  <button type="button" className="secondary" onClick={() => input.current?.click()}>{t('Allega il rapporto (PDF)…')}</button>
                  {rapporto && <span>{rapporto.name}</span>}
                </span>
              </div>
              <label className="field span-6"><span className="field__label">{t('Note')}</span>
                <textarea rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} maxLength={4000} /></label>
            </div>
            <div className="form-dialog__azioni">
              <button type="button" className="secondary" onClick={onChiudi}>{t('Annulla')}</button>
              <button type="button" onClick={() => setRevisione(true)} disabled={!v.performed_on}>{t('Rivedi')}</button>
            </div>
          </>
        )}
      </div>
    </Finestra>
  )
}

/** A failed check proposes taking the device out of service: a person decides. */
function FinestraFuoriServizio({ deviceId, onChiudi, onFatto }: { deviceId: string; onChiudi: () => void; onFatto: () => void }) {
  const [motivo, setMotivo] = useState(t('Verifica non superata'))
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  async function conferma() {
    setErrore(null)
    setLavoro(true)
    try {
      await api.medDeviceUpdate(deviceId, { status: 'out_of_service', status_reason: motivo.trim() || null })
      onFatto()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  return (
    <Finestra titolo={t('Mettere il dispositivo fuori servizio?')} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="form-dialog">
        <p>{t('La verifica non è stata superata. Il dispositivo resta com’è finché non lo decidi tu: fuori servizio, compare così in tutte le viste e negli elenchi.')}</p>
        <div className="form-grid">
          <label className="field span-6"><span className="field__label">{t('Motivo')}</span>
            <input type="text" value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} /></label>
        </div>
        <div className="form-dialog__azioni">
          <button type="button" className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Non ora')}</button>
          <button type="button" onClick={() => void conferma()} disabled={lavoro}>{t('Metti fuori servizio')}</button>
        </div>
      </div>
    </Finestra>
  )
}

/** «Manutenzioni e verifiche» on the device page. */
export function VerificheDispositivo({ deviceId, onCambiato }: { deviceId: string; onCambiato: () => void }) {
  const scrive = useScrive()
  const dati = useAsync(() => api.medDeviceChecks(deviceId), [deviceId])
  const [finestra, setFinestra] = useState<{ tipo: 'piano'; piano: MedPiano | null } | { tipo: 'verifica' } | { tipo: 'fuori' } | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const x = dati.data
  async function esegui(azione: () => Promise<unknown>) {
    setErrore(null)
    try {
      await azione()
      await dati.reload()
    } catch (e) {
      setErrore(e)
    }
  }
  if (dati.loading && !x) return <Spinner />
  if (!x) return <ErrorBanner error={dati.error} />
  return (
    <>
      <ErrorBanner error={errore} />
      {esito && <p className="field__hint" style={{ color: 'var(--ok)' }}>{esito}</p>}
      <Card title={t('Piani')} actions={scrive ? (
        <span className="med-azioni">
          {x.model_profile && (
            <button type="button" className="secondary" onClick={() => void esegui(async () => {
              const r = await api.medPlanDefaults(deviceId)
              setEsito(t('Predefiniti del modello applicati: {n} piani nuovi.', { n: r.plans_created }))
              onCambiato()
            })}>{t('Applica i predefiniti del modello')}</button>
          )}
          <button type="button" className="secondary" onClick={() => setFinestra({ tipo: 'piano', piano: null })}>{t('Nuovo piano…')}</button>
        </span>
      ) : undefined}>
        <DataTable
          rows={x.plans}
          rowKey={(p) => p.id}
          paginazione={false}
          strumenti={false}
          emptyText={t('Nessun piano: quali verifiche, ogni quanto.')}
          columns={[
            { key: 'kind', header: t('Tipo'), value: (p) => etichettaTipoVerifica(p.kind) },
            { key: 'interval', header: t('Ogni'), value: (p) => t('{n} mesi', { n: p.interval_months }) },
            { key: 'next_due', header: t('Prossima scadenza'), type: 'date', value: (p) => p.next_due ?? '', render: (p) => formatDay(p.next_due) },
            { key: 'contract', header: t('Contratto'), value: (p) => p.contract ?? '' },
            { key: 'supplier', header: t('Chi la fa'), value: (p) => p.supplier },
            { key: 'active', header: t('Stato'), value: (p) => (p.active ? t('attivo') : t('sospeso')) },
            ...(scrive ? [{
              key: 'azione', header: '', sort: false, searchable: false,
              render: (p: MedPiano) => (
                <span className="med-azioni">
                  <button type="button" className="secondary" onClick={() => setFinestra({ tipo: 'piano', piano: p })}>{t('Modifica')}</button>
                  <button type="button" className="secondary" onClick={() => {
                    if (window.confirm(t('Togliere il piano «{k}»?', { k: etichettaTipoVerifica(p.kind) }))) void esegui(() => api.medPlanDelete(p.id))
                  }}>{t('Togli')}</button>
                </span>
              ),
            }] : []),
          ]}
        />
      </Card>
      <Card title={t('Verifiche fatte')} actions={scrive ? (
        <button type="button" onClick={() => setFinestra({ tipo: 'verifica' })}>{t('Registra una verifica…')}</button>
      ) : undefined}>
        <DataTable
          rows={x.checks}
          rowKey={(c) => c.id}
          initialSort={{ key: 'performed_on', dir: 'desc' }}
          conteggio={{ nome: t('verifiche') }}
          emptyText={t('Nessuna verifica registrata.')}
          columns={[
            { key: 'performed_on', header: t('Data'), type: 'date', value: (c) => c.performed_on, render: (c) => formatDay(c.performed_on) },
            { key: 'kind', header: t('Tipo'), filter: 'select', value: (c) => etichettaTipoVerifica(c.kind) },
            { key: 'outcome', header: t('Esito'), filter: 'select', value: (c) => etichettaEsito(c.outcome), render: (c) => <BadgeEsito esito={c.outcome} /> },
            { key: 'performed_by', header: t('Eseguita da'), value: (c) => c.performed_by },
            { key: 'measurements', header: t('Misure'), value: (c) => riassuntoMisure(c.measurements) },
            { key: 'files', header: t('Rapporto'), value: (c) => c.files.map((f) => f.name).join(' '),
              render: (c) => c.files.length ? c.files.map((f) => (
                <button key={f.id} type="button" className="linklike" onClick={() => void apriFile(f.id)}>{f.name || t('rapporto')}</button>
              )) : '—' },
            ...(scrive ? [{
              key: 'azione', header: '', sort: false, searchable: false,
              render: (c: MedVerifica) => (
                <button type="button" className="secondary" onClick={() => {
                  if (window.confirm(t('Togliere questa verifica e il suo rapporto?'))) void esegui(() => api.medCheckDelete(c.id))
                }}>{t('Togli')}</button>
              ),
            }] : []),
          ]}
        />
      </Card>
      {finestra?.tipo === 'piano' && (
        <FinestraPiano deviceId={deviceId} piano={finestra.piano} onChiudi={() => setFinestra(null)}
                       onFatto={() => { setFinestra(null); void dati.reload() }} />
      )}
      {finestra?.tipo === 'verifica' && (
        <FinestraVerifica deviceId={deviceId} onChiudi={() => setFinestra(null)}
          onFatto={(fuori) => { setFinestra(fuori ? { tipo: 'fuori' } : null); void dati.reload() }} />
      )}
      {finestra?.tipo === 'fuori' && (
        <FinestraFuoriServizio deviceId={deviceId} onChiudi={() => setFinestra(null)}
                               onFatto={() => { setFinestra(null); onCambiato() }} />
      )}
    </>
  )
}

async function apriFile(id: string) {
  const b = await downloadBlob(api.medFilePath(id))
  const url = URL.createObjectURL(b)
  window.open(url, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/** The model's defaults, in the model card. */
export function ProfiloModello({ chiave }: { chiave: string }) {
  const scrive = useScrive()
  const dati = useAsync(() => api.medModelProfile(chiave), [chiave])
  const [bozza, setBozza] = useState<MedProfiloModello | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [salvato, setSalvato] = useState(false)
  const p = bozza ?? dati.data?.profile ?? { model_key: chiave, plans: [], electrical_class: null, applied_part: null, updated_by: null }
  const tipi = useMemo(() => new Map(p.plans.map((x) => [x.kind, x.interval_months])), [p.plans])
  if (dati.loading && !dati.data) return <Spinner />

  function cambiaPiano(kind: MedTipoVerifica, mesi: string) {
    const altri = p.plans.filter((x) => x.kind !== kind)
    setSalvato(false)
    setBozza({ ...p, plans: mesi ? [...altri, { kind, interval_months: Number(mesi) }] : altri })
  }

  async function salva() {
    setErrore(null)
    try {
      const r = await api.medModelProfileSave(chiave, { plans: p.plans, electrical_class: p.electrical_class, applied_part: p.applied_part })
      setBozza(r.profile)
      setSalvato(true)
    } catch (e) {
      setErrore(e)
    }
  }

  return (
    <>
      <h4 className="form__sezione">{t('Predefiniti del modello')}</h4>
      <p className="field__hint">{t('Ciò che di solito serve a questo modello: si applica a un dispositivo dalla sua scheda, e riempie solo ciò che manca.')}</p>
      <ErrorBanner error={errore} />
      <div className="form-grid">
        {TIPI_VERIFICA.map((k) => (
          <label key={k} className="field"><span className="field__label">{etichettaTipoVerifica(k)}</span>
            <input type="number" min={1} max={120} value={tipi.get(k) ?? ''} placeholder={t('mesi')} disabled={!scrive}
                   onChange={(e) => cambiaPiano(k, e.target.value)} /></label>
        ))}
        <label className="field"><span className="field__label">{t('Classe di protezione')}</span>
          <select value={p.electrical_class ?? ''} disabled={!scrive}
                  onChange={(e) => { setSalvato(false); setBozza({ ...p, electrical_class: e.target.value || null }) }}>
            <option value="">{t('non indicata')}</option>
            {classiElettriche().map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        <label className="field"><span className="field__label">{t('Parte applicata')}</span>
          <select value={p.applied_part ?? ''} disabled={!scrive}
                  onChange={(e) => { setSalvato(false); setBozza({ ...p, applied_part: e.target.value || null }) }}>
            <option value="">{t('non indicata')}</option>
            {partiApplicate().map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
      </div>
      {scrive && (
        <div className="form-dialog__azioni">
          {salvato && <span className="field__hint" style={{ color: 'var(--ok)' }}>{t('Salvato.')}</span>}
          <button type="button" className="secondary" onClick={() => void salva()} disabled={!bozza}>{t('Salva i predefiniti')}</button>
        </div>
      )}
    </>
  )
}

/** The «Manutenzioni e verifiche» view of the area. */
export function VistaVerifiche() {
  const scrive = useScrive()
  const dati = useAsync(() => api.medChecks(), [])
  const avvisi = useAsync(() => api.medNotify(), [])
  const [destinatari, setDestinatari] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const x = dati.data
  if (dati.loading && !x) return <Spinner />
  if (!x) return <ErrorBanner error={dati.error} />
  const conta = (s: MedStatoScadenza) => x.plans.filter((p) => p.due_state === s).length

  async function salvaDestinatari() {
    setErrore(null)
    try {
      await api.medNotifySave((destinatari ?? '').split(/[,;\s]+/).map((d) => d.trim()).filter(Boolean))
      setDestinatari(null)
      await avvisi.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  return (
    <>
      <div className="wazuh-kpi">
        <StatCard label={t('Scadute')} value={conta('overdue')} tone={conta('overdue') ? 'error' : 'ok'} />
        <StatCard label={t('Entro 30 giorni')} value={conta('due_30')} tone={conta('due_30') ? 'warn' : 'ok'} />
        <StatCard label={t('Entro 60 giorni')} value={conta('due_60')} />
        <StatCard label={t('Entro 90 giorni')} value={conta('due_90')} />
        <StatCard label={t('Non superate (90 giorni)')} value={x.failed.length} tone={x.failed.length ? 'error' : 'ok'} />
      </div>
      <Card title={t('Scadenze')}>
        <DataTable
          id="medical-checks"
          rows={x.plans}
          rowKey={(p) => p.id}
          initialSort={{ key: 'next_due', dir: 'asc' }}
          conteggio={{ nome: t('piani') }}
          nomeEsportazione="elettromedicali-scadenze"
          emptyText={t('Nessun piano di verifica: si creano dalla scheda di ogni dispositivo, o dai predefiniti del modello.')}
          columns={[
            { key: 'device', header: t('Dispositivo'), value: (p) => p.device_name,
              render: (p) => <Link to={`/medical/devices/${p.device_id}#manutenzioni`}>{p.device_name || '—'}</Link> },
            { key: 'inventory_number', header: t('N. inventario'), mono: true, value: (p) => p.inventory_number ?? '' },
            { key: 'model', header: t('Modello'), value: (p) => p.model, hidden: true },
            { key: 'org_unit', header: t('Reparto'), filter: 'select', value: (p) => p.org_unit ?? '' },
            { key: 'kind', header: t('Tipo'), filter: 'select', value: (p) => etichettaTipoVerifica(p.kind) },
            { key: 'next_due', header: t('Scadenza'), type: 'date', value: (p) => p.next_due ?? '', render: (p) => formatDay(p.next_due) },
            { key: 'due_state', header: t('Stato'), filter: 'select', value: (p) => etichettaScadenza(p.due_state), render: (p) => <BadgeScadenza stato={p.due_state} /> },
            { key: 'contract', header: t('Contratto'), value: (p) => p.contract ?? '', hidden: true },
            { key: 'supplier', header: t('Chi la fa'), value: (p) => p.supplier, hidden: true },
          ]}
        />
      </Card>
      {x.failed.length > 0 && (
        <Card title={t('Verifiche non superate negli ultimi 90 giorni')}>
          <DataTable rows={x.failed} rowKey={(f) => f.id} paginazione={false} strumenti={false}
            columns={[
              { key: 'device', header: t('Dispositivo'), value: (f) => f.device_name, render: (f) => <Link to={`/medical/devices/${f.device_id}#manutenzioni`}>{f.device_name}</Link> },
              { key: 'kind', header: t('Tipo'), value: (f) => etichettaTipoVerifica(f.kind) },
              { key: 'performed_on', header: t('Data'), type: 'date', value: (f) => f.performed_on, render: (f) => formatDay(f.performed_on) },
              { key: 'status', header: t('Stato del dispositivo'), value: (f) => etichettaStatoMed(f.status), render: (f) => <BadgeStatoMed stato={f.status} /> },
            ]} />
        </Card>
      )}
      <Card title={t('Promemoria')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Una mail al giorno con le verifiche a 60, 30, 7 giorni dalla scadenza e alla scadenza, e ogni settimana quelle scadute. Va ai destinatari delle notifiche del tenant (evento «manutenzioni e verifiche degli elettromedicali», in Impostazioni) e a quelli scritti qui.')}
          {avvisi.data && !avvisi.data.event_enabled && ` ${t('L’evento è spento nelle Impostazioni: non parte nessuna mail.')}`}
        </p>
        <ErrorBanner error={errore || avvisi.error} />
        {scrive ? (
          <div className="med-opzioni">
            <label className="field" style={{ margin: 0, flex: 1, minWidth: 260 }}>
              <span className="field__label">{t('Destinatari dell’area (separati da virgola)')}</span>
              <input type="text" value={destinatari ?? (avvisi.data?.recipients ?? []).join(', ')}
                     onChange={(e) => setDestinatari(e.target.value)} placeholder={t('nome@ospedale.it')} />
            </label>
            <button type="button" className="secondary" onClick={() => void salvaDestinatari()} disabled={destinatari === null}>{t('Salva')}</button>
          </div>
        ) : <p>{(avvisi.data?.recipients ?? []).join(', ') || '—'}</p>}
      </Card>
    </>
  )
}
